/**
 * @fileoverview FlowGrid Identity Manager.
 * @module utils/identity
 *
 * @description
 * Owns the visitor/session identity lifecycle.
 * Writes its own cookies/storage so the SDK can self-bootstrap
 * without depending on an external tracking script (track.js).
 *
 * Backward-compatible: checks for existing track.js `visitor_id`
 * before falling back to its own `fg_visitor_id` key.
 */

import { getCookie, getVisitorId as getTrackJsVisitorId } from './storage';

// ============================================================================
// TYPES
// ============================================================================

export interface IdentityConfig {
  /** Explicit visitor ID (overrides auto-resolution) */
  visitorId?: string;
  /** Cookie domain (defaults to current hostname) */
  cookieDomain?: string;
  /** Visitor cookie max-age in days (default: 365) */
  visitorMaxAgeDays?: number;
  /** Session timeout in minutes (default: 30) */
  sessionTimeoutMinutes?: number;
  /** Storage strategy */
  storage?: 'cookie' | 'localStorage' | 'memory';
  /** Server-side cookie reader (e.g. Next.js `cookies()`) */
  serverCookies?: ServerCookieReader;
  /** Write to `visitor_id` (track.js compat) instead of `fg_visitor_id` (default true) */
  compat?: boolean;
}

/**
 * Minimal interface for reading cookies server-side.
 * Compatible with Next.js `cookies()` and Nuxt `useCookie()`.
 */
export interface ServerCookieReader {
  get(name: string): { value: string } | string | undefined | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Canonical visitor-id form, or null when there is no value at all.
 *
 * Both this SDK and the no-code tracking script write the shared `visitor_id`
 * cookie, and the script rejects anything that isn't a bare UUID. Canonicalizing
 * on read is what stops each writer keeping its own value and splitting the
 * visitor's data in two.
 *
 * DELIBERATELY LENIENT — an unrecognised id is preserved, never discarded.
 * Requiring a UUID here would throw away every id this SDK didn't mint:
 * `visitorId` values customers set themselves, and the legacy `fg_visitor_id`
 * migration path. Discarding a stored id resets a real visitor's identity on
 * upgrade, which is strictly worse than the id-fork this canonicalization
 * exists to prevent.
 *
 * So: strip a legacy `fg_` prefix always, but only lowercase when the remainder
 * IS a UUID. A custom id like `Acct-9F2` is case-significant to whoever issued
 * it and is returned verbatim.
 */
function canonicalVisitorId(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const stripped = /^fg_/i.test(trimmed) ? trimmed.slice(3) : trimmed;
  if (!stripped) return null;
  return UUID_RE.test(stripped) ? stripped.toLowerCase() : stripped;
}

const LEGACY_VISITOR_COOKIE = 'fg_visitor_id';
const COMPAT_VISITOR_COOKIE = 'visitor_id';
const SESSION_KEY = 'fg_session_id';
/** Written by the no-code tracker (flowgrid-core public/flowgrid.js). */
const COMPAT_SESSION_KEY = 'session_id';
const SESSION_TOUCH_KEY = 'fg_session_touch';

// ============================================================================
// IDENTITY MANAGER
// ============================================================================

export class IdentityManager {
  private visitorId: string = '';
  private sessionId: string = '';
  private config: Required<Omit<IdentityConfig, 'serverCookies'>> & { serverCookies?: ServerCookieReader; compat: boolean };

  /** In-memory store for SSR / Node environments */
  private memoryStore: Record<string, string> = {};

  constructor(config?: IdentityConfig) {
    this.config = {
      visitorId: config?.visitorId ?? '',
      cookieDomain: config?.cookieDomain ?? '',
      visitorMaxAgeDays: config?.visitorMaxAgeDays ?? 365,
      sessionTimeoutMinutes: config?.sessionTimeoutMinutes ?? 30,
      storage: config?.storage ?? (this.detectBrowser() ? 'cookie' : 'memory'),
      serverCookies: config?.serverCookies,
      compat: config?.compat ?? true,
    };

    this.visitorId = this.resolveVisitorId();
    this.sessionId = '';
  }

  // ---------------------------------------------------------------------------
  // PUBLIC API
  // ---------------------------------------------------------------------------

  /**
   * Get or create a persistent visitor ID.
   *
   * Resolution order:
   *  1. Explicit config.visitorId
   *  2. track.js `visitor_id` cookie/localStorage (backward compat)
   *  3. `fg_visitor_id` cookie
   *  4. `fg_visitor_id` localStorage
   *  5. Server cookie reader (if provided)
   *  6. Generate new UUID v4, persist to cookie + localStorage
   */
  getVisitorId(): string {
    if (this.visitorId) return this.visitorId;
    this.visitorId = this.resolveVisitorId();
    return this.visitorId;
  }

  /**
   * Get or create a session ID.
   *
   * - Reads from sessionStorage `fg_session_id`
   * - If absent or expired (>sessionTimeoutMinutes since last activity), creates new
   * - Persists to sessionStorage
   * - Touches timestamp on every call
   */
  getSessionId(): string {
    if (this.config.storage === 'memory') {
      return this.getMemorySessionId();
    }

    if (!this.detectBrowser()) {
      return this.getMemorySessionId();
    }

    try {
      const existing = sessionStorage.getItem(SESSION_KEY);
      const lastTouch = sessionStorage.getItem(SESSION_TOUCH_KEY);
      // The no-code tracker's session, when it runs on the same page. It is the
      // only writer of Flowgrid's session table, so its id is the one a
      // payment's `flowgrid_session_id` has to equal to match a visit.
      const tracker = this.readTrackerSessionId();

      if (existing && lastTouch) {
        const elapsed = Date.now() - parseInt(lastTouch, 10);
        const timeoutMs = this.config.sessionTimeoutMinutes * 60 * 1000;
        // A pre-UUID `fgs_…` id is retired rather than kept: nothing in
        // Flowgrid can join on it (replay deletion, for one, requires a UUID).
        if (elapsed < timeoutMs && UUID_RE.test(existing) && (!tracker || tracker === existing)) {
          sessionStorage.setItem(SESSION_TOUCH_KEY, String(Date.now()));
          this.sessionId = existing;
          this.persistSessionCookie(existing);
          return existing;
        }
      }

      // Create new session — or adopt the tracker's, so both scripts agree.
      const newSession = tracker ?? this.generateSessionId();
      sessionStorage.setItem(SESSION_KEY, newSession);
      sessionStorage.setItem(SESSION_TOUCH_KEY, String(Date.now()));
      this.persistSessionCookie(newSession);
      this.sessionId = newSession;
      return newSession;
    } catch {
      return this.getMemorySessionId();
    }
  }

  /**
   * Update the visitor ID (e.g. after identify() links anonymous → known user).
   * Persists to cookie + localStorage.
   */
  setVisitorId(id: string): void {
    this.visitorId = id;
    this.persistVisitorId(id);
  }

  /** Clear session (e.g. on logout). */
  clearSession(): void {
    this.sessionId = '';
    delete this.memoryStore[SESSION_KEY];
    delete this.memoryStore[SESSION_TOUCH_KEY];

    if (this.detectBrowser()) {
      try {
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_TOUCH_KEY);
        this.deleteCookie(SESSION_KEY);
      } catch { /* SSR / privacy mode */ }
    }
  }

  /** Clear all identity (GDPR). Removes visitor + session + legacy experiment assignments. */
  clearAll(): void {
    this.visitorId = '';
    this.sessionId = '';
    this.memoryStore = {};

    if (!this.detectBrowser()) return;

    try {
      // Cookies
      this.deleteCookie(this.visitorCookieName);
      this.deleteCookie(LEGACY_VISITOR_COOKIE);

      // localStorage
      localStorage.removeItem(this.visitorCookieName);
      localStorage.removeItem(LEGACY_VISITOR_COOKIE);
      localStorage.removeItem('fg_exp_assignments');

      // sessionStorage
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_TOUCH_KEY);
    } catch { /* SSR / privacy mode */ }
  }

  /** Check if running in a browser environment. */
  isBrowser(): boolean {
    return this.detectBrowser();
  }

  // ---------------------------------------------------------------------------
  // PRIVATE — VISITOR RESOLUTION
  // ---------------------------------------------------------------------------

  /** Returns the primary cookie name based on compat mode */
  private get visitorCookieName(): string {
    return this.config.compat ? COMPAT_VISITOR_COOKIE : LEGACY_VISITOR_COOKIE;
  }

  private resolveVisitorId(): string {
    // 1. Explicit config
    if (this.config.visitorId) return this.config.visitorId;

    // 2. track.js backward compat (cookie/localStorage `visitor_id`)
    if (this.detectBrowser()) {
      const trackJsId = canonicalVisitorId(getTrackJsVisitorId());
      if (trackJsId) {
        // Migration: if fg_visitor_id exists with a different value, prefer visitor_id (track.js was first)
        const legacyId = this.readCookie(LEGACY_VISITOR_COOKIE) || this.readLocalStorage(LEGACY_VISITOR_COOKIE);
        if (legacyId && legacyId !== trackJsId) {
          // Merge identities by keeping track.js id as canonical
          this.removeLegacyCookie();
        }
        this.persistVisitorId(trackJsId);
        return trackJsId;
      }
    }

    // 3. Migration: fg_visitor_id → visitor_id
    if (this.config.compat) {
      const legacyId = canonicalVisitorId(
        this.readCookie(LEGACY_VISITOR_COOKIE) || this.readLocalStorage(LEGACY_VISITOR_COOKIE),
      );
      if (legacyId) {
        this.persistVisitorId(legacyId);
        this.removeLegacyCookie();
        return legacyId;
      }
    }

    // 4. Own cookie
    const cookieId = canonicalVisitorId(this.readCookie(this.visitorCookieName));
    if (cookieId) return cookieId;

    // 5. localStorage
    if (this.detectBrowser()) {
      try {
        const lsId = canonicalVisitorId(localStorage.getItem(this.visitorCookieName));
        if (lsId) return lsId;
      } catch { /* unavailable */ }
    }

    // 6. Server cookie reader
    if (this.config.serverCookies) {
      const serverVal = this.readServerCookie(this.visitorCookieName);
      if (serverVal) return serverVal;
    }

    // 7. Memory store
    if (this.memoryStore[this.visitorCookieName]) {
      return this.memoryStore[this.visitorCookieName];
    }

    // 8. Generate new ID
    const newId = this.generateVisitorId();
    this.persistVisitorId(newId);
    return newId;
  }

  private readLocalStorage(key: string): string | null {
    if (!this.detectBrowser()) return null;
    try { return localStorage.getItem(key); } catch { return null; }
  }

  private removeLegacyCookie(): void {
    this.deleteCookie(LEGACY_VISITOR_COOKIE);
    if (this.detectBrowser()) {
      try { localStorage.removeItem(LEGACY_VISITOR_COOKIE); } catch { /* */ }
    }
  }

  private persistVisitorId(id: string): void {
    const cookieName = this.visitorCookieName;

    if (this.config.storage === 'memory') {
      this.memoryStore[cookieName] = id;
      return;
    }

    if (!this.detectBrowser()) {
      this.memoryStore[cookieName] = id;
      return;
    }

    // Cookie
    this.writeCookie(cookieName, id, this.config.visitorMaxAgeDays);

    // localStorage as fallback
    try {
      localStorage.setItem(cookieName, id);
    } catch { /* unavailable */ }
  }

  // ---------------------------------------------------------------------------
  // PRIVATE — SESSION HELPERS
  // ---------------------------------------------------------------------------

  /**
   * Mirror the session id to a cookie (in addition to sessionStorage) so
   * server-rendered checkout creation can read it from the request — the
   * Stripe / Lemon Squeezy revenue-attribution cycle passes it through
   * checkout metadata and the payment webhook reads it back out. Same
   * pattern as the no-code tracker's `session_id` cookie mirror. Expiry
   * tracks the session timeout and is refreshed on every activity touch.
   */
  private persistSessionCookie(id: string): void {
    this.writeCookie(SESSION_KEY, id, this.config.sessionTimeoutMinutes / (24 * 60));
  }

  private getMemorySessionId(): string {
    const existing = this.memoryStore[SESSION_KEY];
    const lastTouch = this.memoryStore[SESSION_TOUCH_KEY];

    if (existing && lastTouch) {
      const elapsed = Date.now() - parseInt(lastTouch, 10);
      const timeoutMs = this.config.sessionTimeoutMinutes * 60 * 1000;
      if (elapsed < timeoutMs) {
        this.memoryStore[SESSION_TOUCH_KEY] = String(Date.now());
        this.sessionId = existing;
        return existing;
      }
    }

    const newSession = this.generateSessionId();
    this.memoryStore[SESSION_KEY] = newSession;
    this.memoryStore[SESSION_TOUCH_KEY] = String(Date.now());
    this.sessionId = newSession;
    return newSession;
  }

  // ---------------------------------------------------------------------------
  // PRIVATE — COOKIE OPERATIONS
  // ---------------------------------------------------------------------------

  private readCookie(name: string): string | null {
    // Server-side reader first
    if (this.config.serverCookies) {
      return this.readServerCookie(name);
    }

    // Browser cookie
    if (this.detectBrowser()) {
      return getCookie(name);
    }

    return null;
  }

  private readServerCookie(name: string): string | null {
    if (!this.config.serverCookies) return null;
    const result = this.config.serverCookies.get(name);
    if (!result) return null;
    // Next.js cookies().get() returns { value: string }
    if (typeof result === 'object' && 'value' in result) return result.value || null;
    // Nuxt useCookie() returns string directly
    if (typeof result === 'string') return result || null;
    return null;
  }

  private writeCookie(name: string, value: string, maxAgeDays: number): void {
    if (!this.detectBrowser()) return;

    const maxAge = maxAgeDays * 24 * 60 * 60;
    const domain = this.config.cookieDomain ? `; Domain=${this.config.cookieDomain}` : '';
    const secure = location.protocol === 'https:' ? '; Secure' : '';

    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}${domain}`;
  }

  private deleteCookie(name: string): void {
    if (!this.detectBrowser()) return;
    const domain = this.config.cookieDomain ? `; Domain=${this.config.cookieDomain}` : '';
    document.cookie = `${name}=; Path=/; Max-Age=0${domain}`;
  }

  // ---------------------------------------------------------------------------
  // PRIVATE — ID GENERATION
  // ---------------------------------------------------------------------------

  /**
   * A bare v4 UUID — the CANONICAL visitor id form, shared with the no-code
   * tracking script and the backend.
   *
   * This used to return `fg_<uuid>`, and on any site running both scripts that
   * silently forked the visitor in two. Both writers share the `visitor_id`
   * cookie (compat mode is the default), but the tracking script validates it
   * against a strict UUID regex and REGENERATES anything that fails — so an
   * `fg_`-prefixed value written by this SDK was thrown away and replaced.
   * Whichever script ran second kept its own id in memory, and the visitor
   * ended up with page views under one id and their identity under another:
   * an "identified" person with an empty journey, plus an anonymous ghost
   * carrying all their traffic. Emitting the canonical form means the other
   * script ADOPTS this id instead of overwriting it.
   *
   * The backend strips a legacy `fg_` prefix on read, so bare ids are accepted
   * everywhere the prefixed ones were.
   */
  private generateVisitorId(): string {
    return this.uuidV4();
  }

  /**
   * A UUID, the same form as the no-code tracker's `session_id`.
   *
   * It used to be `fgs_<ms>_<hex>`, which no Flowgrid table could join: the
   * session table only takes the tracker's UUIDs, replay deletion validates a
   * UUID, and the id `checkoutAttribution()` put into Stripe / Lemon Squeezy
   * metadata therefore never matched the visit that paid.
   */
  private generateSessionId(): string {
    return this.uuidV4();
  }

  /** The no-code tracker's current session id (sessionStorage, then its cookie). */
  private readTrackerSessionId(): string | null {
    if (!this.config.compat) return null;
    let id: string | null = null;
    try {
      id = sessionStorage.getItem(COMPAT_SESSION_KEY);
    } catch { /* storage unavailable */ }
    id = id || this.readCookie(COMPAT_SESSION_KEY);
    return id && UUID_RE.test(id) ? id.toLowerCase() : null;
  }

  private uuidV4(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for environments without crypto.randomUUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // ---------------------------------------------------------------------------
  // PRIVATE — ENVIRONMENT DETECTION
  // ---------------------------------------------------------------------------

  private detectBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }
}
